import {
	useCallback,
	useMemo,
	useState
} from "react";
import {
	CommunityPostPollStatistics,
	CommunityPostPollViewModel,
	getPoll,
	resetVote,
	vote
} from "client/Api/Communities/communityPostViewer";
import {
	AlertManager,
	useUnmountedCancellation
} from "client/Hooks";
import { isNil } from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";

type PollDataResultStatus =
	| "pending"
	| "loading"
	| "error"
	| "ready";

type PollDataResult = {
	status: PollDataResultStatus;
	pollData: CommunityPostPollViewModel | null;
	fetchPollData: (signal?: AbortSignal) => void;
	clearPollData: () => void;
	submitVote: (
		optionIds: readonly number[],
		signal: AbortSignal
	) => Promise<TryResult<void>>;
	removeVote: (signal: AbortSignal) => void;
};

type PollDataOptions = {
	postId: number;
	alertManager: AlertManager;
};

export function usePollData(
	options: PollDataOptions
): PollDataResult {
	const {
		postId,
		alertManager
	} = options;

	const [status, setStatus] = useState<PollDataResultStatus>("pending");
	const [pollData, setPollData] = useState<CommunityPostPollViewModel | null>(null);
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const fetchPollDataHandler = useCallback(
		async (signal?: AbortSignal): Promise<void> => {
			setStatus("loading");
			try {
				const result: CommunityPostPollViewModel = await getPoll(
					postId,
					signal ?? globalCancellation
				);

				setPollData(result);
				setStatus("ready");
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				setStatus("error");
			}
		},
		[
			alertManager,
			postId,
			globalCancellation
		]
	);

	const submitVoteHandler = useCallback(
		async (optionIds: readonly number[], signal: AbortSignal): Promise<TryResult<void>> => {
			setStatus("loading");
			try {
				const statistics: CommunityPostPollStatistics = await vote(
					postId,
					{ optionIds },
					signal
				);

				setPollData(
					(data): CommunityPostPollViewModel | null => {
						if (isNil(data)) {
							return data;
						}

						return {
							...data,
							statistics
						};
					}
				);
				setStatus("ready");
				return { wasSuccessful: true, result: undefined };
			} catch (e) {
				if (isCancelledError(e)) {
					return { wasSuccessful: false };
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				setStatus("error");
				return { wasSuccessful: false };
			}
		},
		[
			alertManager,
			postId
		]
	);

	const removeVoteHandler = useCallback(
		async (signal: AbortSignal): Promise<void> => {
			setStatus("loading");
			try {
				const statistics: CommunityPostPollStatistics = await resetVote(
					postId,
					signal
				);

				setPollData(
					(data): CommunityPostPollViewModel | null => {
						if (isNil(data)) {
							return data;
						}

						return {
							...data,
							statistics
						};
					}
				);
				setStatus("ready");
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				setStatus("error");
			}
		},
		[
			alertManager,
			postId
		]
	);

	return useMemo(
		(): PollDataResult => ({
			status,
			pollData,
			fetchPollData: fetchPollDataHandler,
			clearPollData: (): void => setPollData(null),
			removeVote: removeVoteHandler,
			submitVote: submitVoteHandler
		}),
		[
			status,
			pollData,
			fetchPollDataHandler,
			removeVoteHandler,
			submitVoteHandler
		]
	);
}